import React, { useState } from 'react';
import { MessageCircle, Plus } from 'lucide-react';
import type { Review } from '../../types';
import { ReviewCard } from './ReviewCard';
import { ReviewForm } from './ReviewForm';
import { addReview, markReviewHelpful, hasUserReviewed, flagReview } from '../../services/reviewsService';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { EmptyState } from '../common/EmptyState';

interface ReviewSectionProps {
  placeId: string;
  reviews: Review[];
  userId?: string;
  userName?: string;
  userProfilePicture?: string;
  isLoading?: boolean;
  onReviewAdded: (review: Review) => void;
  onShowNotification?: (message: string, type: 'success' | 'info' | 'error') => void;
}

export function ReviewSection({
  placeId,
  reviews,
  userId,
  userName,
  userProfilePicture,
  isLoading = false,
  onReviewAdded,
  onShowNotification,
}: ReviewSectionProps) {
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userHasReviewed, setUserHasReviewed] = useState(false);

  // Check if user has already reviewed
  React.useEffect(() => {
    async function checkReview() {
      if (userId) {
        const hasReviewed = await hasUserReviewed(placeId, userId);
        setUserHasReviewed(hasReviewed);
      }
    }
    checkReview();
  }, [placeId, userId]);

  const handleSubmitReview = async (
    rating: number,
    text: string,
    visitDate?: string
  ) => {
    if (!userId) {
      onShowNotification?.('Bitte melde dich an, um eine Bewertung abzugeben', 'info');
      return;
    }

    setIsSubmitting(true);
    try {
      const reviewId = await addReview({
        placeId,
        userId,
        userName: userName || 'Anonym',
        userProfilePicture,
        rating,
        text,
        visitDate,
      });

      const newReview: Review = {
        id: reviewId,
        placeId,
        userId,
        userName: userName || 'Anonym',
        userProfilePicture,
        rating,
        text,
        visitDate,
        createdAt: new Date(),
        helpful: 0,
        helpfulVoters: [],
      };

      onReviewAdded(newReview);
      setShowForm(false);
      setUserHasReviewed(true);
      onShowNotification?.('Bewertung gespeichert!', 'success');
    } catch (error) {
      console.error('Error submitting review:', error);
      onShowNotification?.('Fehler beim Speichern der Bewertung', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMarkHelpful = async (reviewId: string) => {
    if (!userId) {
      onShowNotification?.('Bitte melde dich an', 'info');
      return;
    }

    try {
      await markReviewHelpful(reviewId, userId);
    } catch (error) {
      console.error('Error marking helpful:', error);
    }
  };

  const handleFlag = async (reviewId: string) => {
    if (!userId) {
      onShowNotification?.('Bitte melde dich an', 'info');
      return;
    }

    try {
      await flagReview(reviewId, userId);
      onShowNotification?.('Bewertung wurde gemeldet', 'success');
    } catch (error) {
      console.error('Error flagging review:', error);
      onShowNotification?.('Fehler beim Melden der Bewertung', 'error');
    }
  };

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-800 flex items-center">
          <MessageCircle className="w-5 h-5 mr-2" />
          Bewertungen ({reviews.length})
        </h3>

        {userId && !userHasReviewed && !showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Bewerten
          </button>
        )}
      </div>

      {/* Review Form */}
      {showForm && (
        <div className="mb-4">
          <ReviewForm
            onSubmit={handleSubmitReview}
            onCancel={() => setShowForm(false)}
            isLoading={isSubmitting}
          />
        </div>
      )}

      {/* Reviews List */}
      {isLoading ? (
        <LoadingSpinner size="md" className="py-8" />
      ) : reviews.length === 0 ? (
        <EmptyState
          type="reviews"
          action={
            userId && !userHasReviewed
              ? { label: 'Erste Bewertung schreiben', onClick: () => setShowForm(true) }
              : undefined
          }
        />
      ) : (
        <div className="space-y-3">
          {reviews.map((review) => (
            <ReviewCard
              key={review.id}
              review={review}
              userId={userId}
              onMarkHelpful={handleMarkHelpful}
              onFlag={handleFlag}
            />
          ))}
        </div>
      )}

      {/* Login prompt for non-logged in users */}
      {!userId && reviews.length > 0 && (
        <p className="text-center text-sm text-gray-500 mt-4">
          Melde dich an, um eine Bewertung zu schreiben
        </p>
      )}
    </div>
  );
}
